import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getVoyage, updateVoyage, deleteVoyage } from "@/lib/voyagesApi";
import { listTargets, listRestaurants } from "@/api/mealPlanApi";
import {
  buildVoyagePdfUrl,
  buildVoyageXlsxUrl,
  buildVoyageJsonExportUrl,
  buildVoyageBundleExportUrl,
} from "@/lib/settingsApi";
import { formatDate, endDate } from "@/lib/dateHelpers";
import { Button } from "@/components/ui/button";
import AutoBackupPanel from "@/components/AutoBackupPanel";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import DaysTable from "@/components/DaysTable";
import DuplicateDialog from "@/components/DuplicateDialog";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import AdvancedPdfFilterDialog from "@/components/AdvancedPdfFilterDialog";
import MealPlanEditor from "@/components/mealplan/MealPlanEditor";
import TargetsPanel from "@/components/mealplan/TargetsPanel";
import { toast } from "@/hooks/use-toast";
import {
  ArrowLeft, Loader2, Save, Copy, Trash2, Ship, CalendarClock,
  Download, FileText, FileSpreadsheet, LayoutGrid, ChevronDown,
  SlidersHorizontal, Soup, UtensilsCrossed, Coffee, CalendarRange, Cookie,
  Package, FileJson,
} from "lucide-react";

const STATUS_OPTIONS = ["draft", "active", "archived"];
const statusColor = (s) => {
  if (s === "active") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "archived") return "bg-gray-100 text-gray-600 border-gray-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

export default function VoyageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [voyage, setVoyage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Header form
  const [header, setHeader] = useState({});
  const [headerSaving, setHeaderSaving] = useState(false);

  // Days
  const [days, setDays] = useState([]);
  const [daysSaving, setDaysSaving] = useState(false);
  const [totalDaysInput, setTotalDaysInput] = useState(0);

  // Phase 7+++ — voyage-level Targets moved here from the Meal Plan tab.
  // Lives alongside the header form and saves through the same
  // `Save Changes` button via updateVoyage(id, { ..., targets }).
  const [targets, setTargets] = useState([]);
  const [targetsLib, setTargetsLib] = useState([]);

  // PDF export
  const [exporting, setExporting] = useState(false);
  // Phase 8+1 — filtered exports.
  const [restaurants, setRestaurants] = useState([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  // Phase 9 — dedicated Canapé Function PDF day picker.
  const [canapeDayDialogOpen, setCanapeDayDialogOpen] = useState(false);
  // Hotfix 2 — paper size selector for the Canapé Function PDF.
  // Mirrors the same preference key the /print/stations page uses so a
  // single localStorage entry serves both routes.
  const [canapePaper, setCanapePaper] = useState(() => {
    try { return localStorage.getItem("vm:print_paper") || "a4"; }
    catch { return "a4"; }
  });
  useEffect(() => {
    try { localStorage.setItem("vm:print_paper", canapePaper); }
    catch { /* no-op */ }
  }, [canapePaper]);

  const handleResolveExportCanape = (dayNum) => {
    if (!voyage) return;
    try {
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams();
      if (token) params.set("token", token);
      // Only emit `paper=` when the user explicitly picked Letter so
      // the legacy A4 default is preserved when omitted.
      if (canapePaper === "letter") params.set("paper", "letter");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const a = document.createElement("a");
      a.href = `${process.env.REACT_APP_BACKEND_URL || ""}/api/voyages/${voyage.id}/days/${dayNum}/export/canape-function-pdf${qs}`;
      // Defer filename to server's Content-Disposition.
      a.download = "";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setCanapeDayDialogOpen(false);
      toast({
        title: "Generating Canapé Function PDF",
        description: `Day ${dayNum} — download should start shortly.`,
      });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e?.message || "Could not start canapé PDF download",
        variant: "destructive",
      });
    }
  };

  // Dialogs
  const [dupOpen, setDupOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const fetchVoyage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVoyage(id);
      setVoyage(data);
      setHeader({
        voyage_number: data.voyage_number,
        ship_name: data.ship_name || "",
        start_date: data.start_date,
        guest_count: data.guest_count || 0,
        kids_count: data.kids_count || 0,
        status: data.status,
      });
      setDays(data.days || []);
      setTotalDaysInput(data.total_days);
      setTargets(data.targets || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchVoyage(); }, [fetchVoyage]);

  // Phase 7+++ — fetch the targets library once so the panel can offer
  // "Sync from library" and the lib picker. Independent of the voyage
  // fetch so failures don't block the page load. `listTargets` is an
  // imported module-scoped function — stable reference, intentionally
  // not in the dep array (ESLint exhaustive-deps correctly treats it
  // as an outer-scope value).
  useEffect(() => {
    (async () => {
      try {
        const lib = await listTargets();
        setTargetsLib(lib || []);
      } catch (e) {
        console.warn("VoyageDetail: listTargets failed — continuing without targets lib", e);
        setTargetsLib([]);
      }
    })();
  }, []);

  // Phase 8+1 — load enabled restaurants once so the Advanced PDF filter
  // dialog has the venue list ready to render. Failure is non-fatal —
  // the dropdown presets still work without restaurants. Same rationale
  // as above for the empty dep array.
  useEffect(() => {
    (async () => {
      try {
        const list = await listRestaurants();
        setRestaurants(Array.isArray(list) ? list.filter((r) => r.enabled !== false) : []);
      } catch (e) {
        console.warn("VoyageDetail: listRestaurants failed — filter dialog will have an empty venue list", e);
        setRestaurants([]);
      }
    })();
  }, []);

  const handleHeaderSave = async () => {
    setHeaderSaving(true);
    try {
      const payload = {
        voyage_number: header.voyage_number,
        ship_id: null,
        ship_name: header.ship_name,
        start_date: header.start_date,
        total_days: totalDaysInput,
        guest_count: header.guest_count,
        kids_count: header.kids_count,
        status: header.status,
        // Phase 7+++ — Targets now save through the Overview tab's
        // Save Changes button (moved off the Meal Plan tab).
        targets,
      };
      const updated = await updateVoyage(id, payload);
      setVoyage(updated);
      setDays(updated.days || []);
      setTotalDaysInput(updated.total_days);
      setTargets(updated.targets || []);
      toast({ title: "Voyage updated", description: "Header + targets saved successfully." });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: "Save failed", description: typeof detail === "string" ? detail : JSON.stringify(detail), variant: "destructive" });
    } finally {
      setHeaderSaving(false);
    }
  };

  // Triggers a browser download by creating a temporary <a download> link.
  // Using a real <a> (not window.open) keeps the user on the page and lets
  // the browser surface the file save dialog/download row natively.
  //
  // `opts`:
  //   - layout:   "per_day" (default) | "single_page"
  //   - sections: optional string[] of canonical section keys
  //   - venues:   optional string[] of lib_restaurants ids
  //
  // Phase 8+1 — when a filter is supplied we DROP `a.download` (set to
  // empty string) so the browser respects the server's `Content-
  // Disposition: attachment; filename="..."` which embeds the filter
  // descriptor (e.g. `_Specialty.pdf`). For full exports we keep the
  // client-side filename for backwards compatibility.
  const handleExportPdf = (opts = {}) => {
    if (!voyage || exporting) return;
    const { layout = "per_day", sections, venues, buffet_layout } = opts;
    const filtered = (Array.isArray(sections) && sections.length > 0)
      || (Array.isArray(venues) && venues.length > 0);
    setExporting(true);
    try {
      // Phase 14 — fall back to localStorage when the caller (e.g. the
      // top-bar quick-export button that bypasses the filter dialog)
      // doesn't pass a buffet_layout. Keeps the user's last choice
      // honoured even on the unfiltered export path.
      let resolvedBuffetLayout = buffet_layout;
      if (!resolvedBuffetLayout) {
        try {
          const stored = window.localStorage.getItem("vm:buffet_layout");
          if (stored === "line" || stored === "kitchen") {
            resolvedBuffetLayout = stored;
          }
        } catch { /* private mode */ }
      }
      const url = buildVoyagePdfUrl(voyage.id, {
        layout, sections, venues,
        buffet_layout: resolvedBuffetLayout,
      });
      const a = document.createElement("a");
      a.href = url;
      if (filtered) {
        // Empty string = "ask the server" via Content-Disposition.
        a.download = "";
      } else {
        const suffix = layout === "single_page" ? "-single-page" : "";
        a.download = `voyage-${voyage.voyage_number}${suffix}.pdf`;
      }
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Generating PDF",
        description: filtered
          ? "Filtered PDF download should start shortly."
          : layout === "single_page"
            ? "Single-page overview download should start shortly."
            : "Per-day PDF download should start shortly.",
      });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e?.message || "Could not start PDF download",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setExporting(false), 2500);
    }
  };

  // Phase 7+/C — Excel (.xlsx) export. Streams a multi-sheet workbook
  // (1 overview + N day sheets). Same auth-via-?token= pattern as the
  // PDF export, same loading/error UX so the two buttons feel paired.
  const handleExportXlsx = () => {
    if (!voyage || exporting) return;
    setExporting(true);
    try {
      // Phase 14 — same localStorage fall-through so the user's
      // last-picked buffet layout applies to Excel exports too.
      let resolvedBuffetLayout = "kitchen";
      try {
        const stored = window.localStorage.getItem("vm:buffet_layout");
        if (stored === "line" || stored === "kitchen") {
          resolvedBuffetLayout = stored;
        }
      } catch { /* private mode */ }
      const url = buildVoyageXlsxUrl(voyage.id, {
        buffet_layout: resolvedBuffetLayout,
      });
      const a = document.createElement("a");
      a.href = url;
      a.download = `voyage-${voyage.voyage_number}.xlsx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Generating Excel",
        description:
          "Multi-sheet .xlsx download should start shortly (one sheet per voyage day).",
      });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e?.message || "Could not start Excel download",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setExporting(false), 2500);
    }
  };

  const handleDaysSave = async () => {
    setDaysSaving(true);
    try {
      const payload = { days, total_days: days.length };
      const updated = await updateVoyage(id, payload);
      setVoyage(updated);
      setDays(updated.days || []);
      toast({ title: "Days updated", description: "Day schedule saved successfully." });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: "Save failed", description: typeof detail === "string" ? detail : JSON.stringify(detail), variant: "destructive" });
    } finally {
      setDaysSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDelLoading(true);
    try {
      await deleteVoyage(id);
      navigate("/voyages");
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setDelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !voyage) {
    return (
      <div className="px-6 py-8">
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
          {error || "Voyage not found"}
        </div>
        <Link to="/voyages"><Button variant="outline" className="mt-4"><ArrowLeft className="h-4 w-4" /> Back</Button></Link>
      </div>
    );
  }

  return (
    <div className="px-6 py-8" data-testid="voyage-detail-page">
      {/* Top bar */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/voyages">
            <Button variant="ghost" size="icon" aria-label="Back to voyages">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight" data-testid="voyage-detail-title">
                {voyage.voyage_number}
              </h1>
              <Badge variant="outline" className={statusColor(voyage.status)}>
                {voyage.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Ship className="h-3.5 w-3.5" /> {voyage.ship_name || "—"}
              <span className="mx-1">·</span>
              <CalendarClock className="h-3.5 w-3.5" />
              {formatDate(voyage.start_date)} → {formatDate(endDate(voyage.start_date, voyage.total_days))}
              <span className="mx-1">·</span>
              {voyage.total_days} days
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase 16 (F7/CO2) — Quick Picker grid view. */}
          <Link to={`/voyages/${voyage.id}/quick-picker`}>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
              data-testid="quick-picker-btn"
            >
              <CalendarClock className="h-4 w-4" />
              Quick Picker
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export PDF
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                Full export
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ layout: "per_day" })}
                data-testid="export-pdf-per-day-btn"
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Export Full PDF</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    Everything, one A2 page per day.
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ layout: "single_page" })}
                data-testid="export-pdf-single-page-btn"
                className="gap-2"
              >
                <LayoutGrid className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">All in one page (overview)</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    Every day as a column on ONE A2 page.
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                Section-only presets
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ sections: ["regular_restaurants"] })}
                data-testid="export-pdf-preset-regular-btn"
                className="gap-2"
              >
                <UtensilsCrossed className="h-4 w-4" />
                Regular Restaurants only
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ sections: ["specialty_restaurants"] })}
                data-testid="export-pdf-preset-specialty-btn"
                className="gap-2"
              >
                <UtensilsCrossed className="h-4 w-4" />
                Specialty Restaurants only
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ sections: ["buffets"] })}
                data-testid="export-pdf-preset-buffets-btn"
                className="gap-2"
              >
                <Soup className="h-4 w-4" />
                All Buffets only
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPdf({ sections: ["crew_mess_buffet"] })}
                data-testid="export-pdf-preset-crewmess-btn"
                className="gap-2"
              >
                <Coffee className="h-4 w-4" />
                Crew Mess Buffet only
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPdf({
                  sections: ["daily_events", "special_events", "chefs_special"],
                })}
                data-testid="export-pdf-preset-events-btn"
                className="gap-2"
              >
                <CalendarRange className="h-4 w-4" />
                Events &amp; Chef's Special only
              </DropdownMenuItem>
              {/* Phase 9 — Canapé Functions preset. Pure section filter,
                  no per-venue sub-set required (canapé items are not
                  modelled as `lib_restaurants` venues). */}
              <DropdownMenuItem
                onClick={() => handleExportPdf({ sections: ["canape_functions"] })}
                data-testid="export-pdf-preset-canape-btn"
                className="gap-2"
              >
                <Cookie className="h-4 w-4" />
                Canapé Functions only
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setFilterDialogOpen(true)}
                data-testid="export-pdf-advanced-btn"
                className="gap-2"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Advanced filter…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Phase 7+/C — Excel (.xlsx) workbook download. Sits next to
              the PDF dropdown so the two exporters feel paired. */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportXlsx}
            disabled={exporting}
            data-testid="export-xlsx-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            Export Excel
          </Button>
          {/* Phase 9 — Dedicated Canapé Function PDF. Opens the day
              picker dialog; the actual download fires from there.
              Hotfix Fix-2 — render an INLINE paper-size selector next
              to the trigger button as well, so the tester (and user)
              can see/change A4↔Letter without opening the modal. Both
              the inline selector and the modal-internal selector bind
              to the same `canapePaper` state. */}
          <div
            className="inline-flex items-center gap-1 rounded-md border bg-background px-1.5 py-0.5"
            data-testid="canape-paper-size-inline-wrap"
          >
            <label
              htmlFor="canape-paper-inline"
              className="text-[10px] font-medium uppercase tracking-wide text-slate-600"
            >
              Paper
            </label>
            <select
              id="canape-paper-inline"
              value={canapePaper}
              onChange={(e) => setCanapePaper(e.target.value)}
              className="h-7 rounded border border-input bg-background px-1 text-xs"
              data-testid="canape-paper-size-select"
            >
              <option value="a4">A4</option>
              <option value="letter">Letter</option>
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCanapeDayDialogOpen(true)}
            disabled={exporting}
            data-testid="export-canape-fn-btn"
          >
            <Cookie className="h-4 w-4" />
            Export Canapé Function
          </Button>
          {/* Phase 10 — Export Voyage dropdown (JSON or Bundle ZIP). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                data-testid="export-voyage-btn"
              >
                <Package className="h-4 w-4" />
                Export Voyage
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                Portable voyage export
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  const url = buildVoyageJsonExportUrl(voyage.id);
                  const a = document.createElement("a");
                  a.href = url;
                  a.rel = "noopener";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                data-testid="export-voyage-json-btn"
                className="gap-2"
              >
                <FileJson className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Voyage Only (JSON)</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    Smallest; opens on any server thanks to drift-tolerant validators.
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const url = buildVoyageBundleExportUrl(voyage.id);
                  const a = document.createElement("a");
                  a.href = url;
                  a.rel = "noopener";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                data-testid="export-voyage-bundle-btn"
                className="gap-2"
              >
                <Package className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Voyage Bundle (ZIP)</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    Voyage + referenced library entries + canapé photos. Self-contained.
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={() => setDupOpen(true)} data-testid="voyage-duplicate-btn">
            <Copy className="h-4 w-4" /> Duplicate
          </Button>
          {/* F16 — auto-backup restore panel (P0 safety net) */}
          <AutoBackupPanel voyageId={id} onRestored={fetchVoyage} />
          <Button variant="destructive" size="sm" onClick={() => setDelOpen(true)} data-testid="voyage-delete-btn">
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="days" data-testid="tab-days">Days ({days.length})</TabsTrigger>
          <TabsTrigger value="mealplan" data-testid="tab-mealplan">Meal Plan</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-3xl">
            <div className="space-y-1.5">
              <Label htmlFor="det-voyage-number">Voyage Number</Label>
              <Input id="det-voyage-number" value={header.voyage_number || ""} onChange={(e) => setHeader((h) => ({ ...h, voyage_number: e.target.value }))} data-testid="det-voyage-number" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="det-ship-name">Ship</Label>
              <Input
                id="det-ship-name"
                value={header.ship_name || ""}
                onChange={(e) => setHeader((h) => ({ ...h, ship_name: e.target.value }))}
                placeholder="Ship name"
                data-testid="det-ship-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="det-start-date">Start Date</Label>
              <Input id="det-start-date" type="date" value={header.start_date || ""} onChange={(e) => setHeader((h) => ({ ...h, start_date: e.target.value }))} data-testid="det-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="det-total-days">Total Days</Label>
              <Input id="det-total-days" type="number" min={1} max={30} value={totalDaysInput} onChange={(e) => setTotalDaysInput(parseInt(e.target.value, 10) || 1)} data-testid="det-total-days" />
              {totalDaysInput !== (voyage.total_days) && (
                <p className="text-xs text-amber-600">
                  {totalDaysInput > voyage.total_days
                    ? `Will add ${totalDaysInput - voyage.total_days} trailing Sea day(s)`
                    : `Will remove ${voyage.total_days - totalDaysInput} trailing day(s)`}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="det-guest-count">Guest Count</Label>
              <Input id="det-guest-count" type="number" min={0} value={header.guest_count} onChange={(e) => setHeader((h) => ({ ...h, guest_count: parseInt(e.target.value, 10) || 0 }))} data-testid="det-guest-count" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="det-kids-count">Kids Count</Label>
              <Input id="det-kids-count" type="number" min={0} value={header.kids_count} onChange={(e) => setHeader((h) => ({ ...h, kids_count: parseInt(e.target.value, 10) || 0 }))} data-testid="det-kids-count" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={header.status} onValueChange={(v) => setHeader((h) => ({ ...h, status: v }))}>
                <SelectTrigger data-testid="det-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Phase 7+++ — Targets moved here from the Meal Plan tab.
              They're voyage-level metrics (NPS, Food Quality, ATRI, …),
              not per-day, so they belong with the voyage header. */}
          <div className="mt-6 max-w-3xl">
            <TargetsPanel
              value={targets}
              onChange={setTargets}
              targetsLib={targetsLib}
            />
          </div>
          <Button className="mt-6" onClick={handleHeaderSave} disabled={headerSaving} data-testid="voyage-header-save">
            {headerSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </TabsContent>

        {/* Days Tab */}
        <TabsContent value="days" className="mt-4">
          <DaysTable days={days} onChange={setDays} />
          <Button className="mt-4" onClick={handleDaysSave} disabled={daysSaving} data-testid="voyage-days-save">
            {daysSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Days
          </Button>
        </TabsContent>

        {/* Meal Plan Tab — Phase 3 day editor */}
        <TabsContent value="mealplan" className="mt-4">
          <MealPlanEditor voyage={voyage} onExportPdf={handleExportPdf} exporting={exporting} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <DuplicateDialog open={dupOpen} onOpenChange={setDupOpen} voyage={voyage} onSuccess={() => { setDupOpen(false); fetchVoyage(); }} />
      <DeleteConfirmDialog open={delOpen} onOpenChange={setDelOpen} itemName={voyage.voyage_number} loading={delLoading} onConfirm={handleDeleteConfirm} />
      {/* Phase 8+1 — Advanced PDF filter dialog */}
      <AdvancedPdfFilterDialog
        open={filterDialogOpen}
        onOpenChange={setFilterDialogOpen}
        restaurants={restaurants}
        onExport={(payload) => handleExportPdf(payload)}
      />
      {/* Phase 9 — Canapé Function day picker. Renders inline rather
          than as a separate component because the picker is so small:
          one button per day with date + port shown. Click → start the
          download via `handleResolveExportCanape(dayNum)`. */}
      {canapeDayDialogOpen && voyage?.days && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setCanapeDayDialogOpen(false)}
          data-testid="canape-day-picker-backdrop"
        >
          <div
            className="bg-background rounded-md border shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="canape-day-picker-dialog"
          >
            <div className="flex items-center gap-2 mb-3">
              <Cookie className="h-4 w-4" />
              <h3 className="text-base font-semibold">Pick a day for the Canapé Function PDF</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              The PDF will contain every canapé function event on that day with photos,
              ingredients, and a daily totals roll-up.
            </p>
            {/* Hotfix 2 — paper size selector for the Canapé Function PDF. */}
            <div className="mb-3 flex items-center gap-2 rounded border bg-slate-50/50 px-2 py-1.5">
              <label
                htmlFor="canape-paper-select"
                className="text-xs font-medium text-slate-700"
              >
                Paper size:
              </label>
              <select
                id="canape-paper-select"
                value={canapePaper}
                onChange={(e) => setCanapePaper(e.target.value)}
                className="h-7 rounded border border-input bg-background px-1.5 text-xs"
                data-testid="canape-paper-size-select-modal"
              >
                <option value="a4">A4</option>
                <option value="letter">Letter</option>
              </select>
            </div>
            <div className="max-h-[60vh] overflow-y-auto flex flex-col gap-1.5">
              {(voyage.days || []).map((d) => (
                <Button
                  key={`canape-day-${d.day_number}`}
                  variant="outline"
                  size="sm"
                  className="justify-start gap-2 w-full text-left"
                  onClick={() => handleResolveExportCanape(d.day_number)}
                  data-testid={`canape-day-pick-${d.day_number}`}
                >
                  <span className="font-semibold">Day {d.day_number}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{d.date}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="truncate">
                    {(d.port_or_sea || "").toLowerCase() === "sea"
                      ? "Sea Day"
                      : (d.port_name || "—")}
                  </span>
                </Button>
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <Button
                variant="ghost" size="sm"
                onClick={() => setCanapeDayDialogOpen(false)}
                data-testid="canape-day-picker-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
